import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { Language, Refresh, Search, ContentCopy, OpenInNew } from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";
import SimilarityReportDialog, { similarityColors } from "./components/SimilarityReportDialog";
import BulkWebCheckDialog from "./components/BulkWebCheckDialog";

const TYPE_LABELS = { mock_exam: "Mock exam", paper: "Past paper" };

const METRIC_OPTIONS = [
  { value: "max", label: "Highest match (either)" },
  { value: "internal", label: "Other papers (internal)" },
  { value: "web", label: "Other websites (web)" },
];

const LEVEL_OPTIONS = [
  { value: "", label: "All levels" },
  { value: "high", label: "High (50%+)" },
  { value: "warn", label: "Some (20–50%)" },
  { value: "low", label: "Low (under 20%)" },
  { value: "unchecked", label: "Not checked" },
];

/** Percentage chip in the shared similarity colours; dashed when not checked. */
const PercentChip = ({ percent, status, thresholds, title }) => {
  if (status === "pending") return <Chip size="small" label="Checking…" sx={{ height: 22, fontSize: "0.7rem" }} />;
  if (status === "no_index") {
    return (
      <Tooltip title="Checked before any imported papers were indexed, so there was nothing to compare against. Run: php artisan similarity:backfill --imports, then --mock-exams --papers.">
        <Chip size="small" label="Not indexed" variant="outlined"
          sx={{ height: 22, fontSize: "0.7rem", color: "#e65100", borderColor: "#ffb74d", borderStyle: "dashed" }} />
      </Tooltip>
    );
  }
  if (percent == null) {
    return (
      <Chip size="small" label="Not checked" variant="outlined"
        sx={{ height: 22, fontSize: "0.7rem", color: "#9e9e9e", borderStyle: "dashed" }} />
    );
  }
  const c = similarityColors(percent, thresholds);
  const chip = <Chip size="small" label={`${Number(percent).toFixed(1)}%`} sx={{ height: 22, fontWeight: 700, bgcolor: c.bgcolor, color: c.color }} />;
  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip;
};

const StatCard = ({ icon, title, subtitle, stats, total, thresholds }) => {
  const levels = [
    { key: "high", label: `High ${thresholds.high}%+`, percent: thresholds.high },
    { key: "warn", label: `Some ${thresholds.warn}–${thresholds.high}%`, percent: thresholds.warn },
    { key: "low", label: `Low <${thresholds.warn}%`, percent: 0 },
  ];
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          {icon}
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 1.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>{stats.checked}</Typography>
          <Typography variant="body2" color="text.secondary">
            of {total} checked{stats.average != null ? ` · average ${stats.average}%` : ""}
          </Typography>
        </Box>
        <LinearProgress variant="determinate" value={total ? (100 * stats.checked) / total : 0} sx={{ my: 1.25, height: 6, borderRadius: 3 }} />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {levels.map((l) => {
            const c = similarityColors(l.percent, thresholds);
            return (
              <Chip key={l.key} size="small" label={`${l.label}: ${stats[l.key]}`}
                sx={{ fontWeight: 600, bgcolor: c.bgcolor, color: c.color }} />
            );
          })}
          <Chip size="small" variant="outlined" label={`Not checked: ${stats.unchecked}`} sx={{ borderStyle: "dashed", color: "#757575" }} />
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * Similarity Summary: every mock exam and past paper ranked by how much of its content
 * matches other papers (internal check) or other websites (web check), highest first.
 * Data: GET /admin/similarity-summary (SimilaritySummaryService on the backend).
 */
export default function SimilaritySummary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("all");
  const [metric, setMetric] = useState("max");
  const [level, setLevel] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [reportTarget, setReportTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ metric, page: page + 1, per_page: perPage });
      if (type !== "all") params.append("type", type);
      if (level) params.append("level", level);
      if (appliedSearch) params.append("search", appliedSearch);
      const res = await api.get(`/admin/similarity-summary?${params.toString()}`);
      setData(res.data?.data || null);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load the similarity summary");
    } finally {
      setLoading(false);
    }
  }, [type, metric, level, appliedSearch, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      setAppliedSearch(search.trim());
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const thresholds = data?.thresholds || { warn: 20, high: 50 };
  const total = Object.values(data?.stats?.by_type || {}).reduce((a, b) => a + b, 0);
  const resetPage = (setter) => (value) => { setter(value); setPage(0); };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
        Question Bank /{" "}
        <Box component="span" sx={{ color: "#3B2A9F", fontWeight: 600 }}>Similarity Summary</Box>
      </Typography>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 0.5, mb: 2, flexWrap: "wrap" }}>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <Typography variant="h5" fontWeight={700}>Similarity Summary</Typography>
          <Typography variant="body2" color="text.secondary">
            Mock exams and past papers ranked by how much of their content matches other papers or other websites. Highest first.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading} sx={{ textTransform: "none" }}>
          Refresh
        </Button>
        <Button variant="contained" startIcon={<Language />} onClick={() => setBulkOpen(true)} sx={{ textTransform: "none" }}>
          Web check all
        </Button>
      </Box>

      {loading && !data && <LinearProgress />}

      {data && (
        <>
          {data.stats.internal.no_index > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>{data.stats.internal.no_index}</strong> item{data.stats.internal.no_index === 1 ? " was" : "s were"} checked
              before any imported papers were indexed, so their "Other papers" score is missing (shown as <em>Not indexed</em>).
              Run <code>php artisan similarity:backfill --imports</code> and then{" "}
              <code>php artisan similarity:backfill --mock-exams --papers</code> on the server.
            </Alert>
          )}

          {/* Overview */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<ContentCopy fontSize="small" color="action" />}
                title="Other papers"
                subtitle="Content that already exists in other imported papers"
                stats={data.stats.internal}
                total={total}
                thresholds={thresholds}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                icon={<Language fontSize="small" color="action" />}
                title="Other websites"
                subtitle="Content found published on other websites"
                stats={data.stats.web}
                total={total}
                thresholds={thresholds}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Top websites</Typography>
                  <Typography variant="caption" color="text.secondary">Where our papers were found most often</Typography>
                  {data.top_domains?.length ? (
                    <Box sx={{ mt: 1 }}>
                      {data.top_domains.slice(0, 6).map((d) => (
                        <Box key={d.domain} sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.4 }}>
                          <Link href={`https://${d.domain}`} target="_blank" rel="noopener noreferrer" underline="hover"
                            sx={{ flex: 1, fontSize: "0.85rem", fontWeight: 600, wordBreak: "break-all" }}>
                            {d.domain}
                          </Link>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            {d.papers} paper{d.papers === 1 ? "" : "s"}
                          </Typography>
                          {d.max_percent != null && <PercentChip percent={d.max_percent} thresholds={thresholds} />}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                      No web checks yet. Use <strong>Web check all</strong>.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Filters */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", "&:last-child": { pb: 2 } }}>
              <ToggleButtonGroup size="small" exclusive value={type} onChange={(e, v) => v && resetPage(setType)(v)}>
                <ToggleButton value="all" sx={{ textTransform: "none" }}>All ({total})</ToggleButton>
                <ToggleButton value="mock_exam" sx={{ textTransform: "none" }}>Mock exams ({data.stats.by_type?.mock_exam ?? 0})</ToggleButton>
                <ToggleButton value="paper" sx={{ textTransform: "none" }}>Past papers ({data.stats.by_type?.paper ?? 0})</ToggleButton>
              </ToggleButtonGroup>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Rank by</InputLabel>
                <Select value={metric} label="Rank by" onChange={(e) => resetPage(setMetric)(e.target.value)}>
                  {METRIC_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>Level</InputLabel>
                <Select value={level} label="Level" onChange={(e) => resetPage(setLevel)(e.target.value)}>
                  {LEVEL_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                size="small"
                placeholder="Search by name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flex: 1, minWidth: 200 }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
              />
            </CardContent>
          </Card>

          {/* Ranking */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            {loading && <LinearProgress />}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, whiteSpace: "nowrap" } }}>
                    <TableCell width={48}>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Other papers</TableCell>
                    <TableCell>Other websites</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.map((r, i) => (
                    <TableRow key={`${r.type}-${r.id}`} hover sx={{ cursor: "pointer" }} onClick={() => setReportTarget(r)}>
                      <TableCell sx={{ color: "text.secondary", fontWeight: 600 }}>{page * perPage + i + 1}</TableCell>
                      <TableCell sx={{ maxWidth: 360 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>{r.name || `#${r.id}`}</Typography>
                        <Box sx={{ display: "flex", gap: 0.5, mt: 0.25 }}>
                          <Chip size="small" label={TYPE_LABELS[r.type]} sx={{ height: 18, fontSize: "0.65rem" }}
                            color={r.type === "mock_exam" ? "primary" : "default"} variant="outlined" />
                          <Chip size="small" label={`#${r.id}`} variant="outlined" sx={{ height: 18, fontSize: "0.65rem", fontFamily: "monospace" }} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <PercentChip percent={r.internal_percent} status={r.internal_status} thresholds={thresholds} />
                        {r.internal_top_source && r.internal_percent > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, wordBreak: "break-word" }}>
                            top: {r.internal_top_source}
                          </Typography>
                        )}
                        {r.original_percent != null && r.original_percent > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {Number(r.original_percent).toFixed(0)}% from its original
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        <PercentChip
                          percent={r.web_score}
                          status={r.web_status}
                          thresholds={thresholds}
                          title={r.web_score != null
                            ? `Text overlap with the pages we could read: ${Number(r.web_percent).toFixed(1)}%. Searched sentences found word for word online: ${r.web_phrases_found} of ${r.web_phrases_searched}${r.web_found_percent != null ? ` (${r.web_found_percent}%)` : ""}. The higher of the two is shown.`
                            : undefined}
                        />
                        {r.web_top_domain && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
                            top:{" "}
                            <Link href={r.web_top_url} target="_blank" rel="noopener noreferrer" underline="hover"
                              onClick={(e) => e.stopPropagation()} sx={{ wordBreak: "break-all" }}>
                              {r.web_top_domain}
                            </Link>
                            <OpenInNew sx={{ fontSize: "0.8rem" }} />
                          </Typography>
                        )}
                        {r.web_phrases_searched > 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {r.web_phrases_found} of {r.web_phrases_searched} sentences found online
                            {r.web_found_percent > r.web_percent && r.web_percent < 1 ? " · page text not readable" : ""}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Open the full report">
                          <Button size="small" sx={{ textTransform: "none" }}>Report</Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!data.rows.length && (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                        Nothing matches these filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={data.total}
              page={page}
              onPageChange={(e, p) => setPage(p)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Card>
        </>
      )}

      <SimilarityReportDialog
        open={Boolean(reportTarget)}
        endpointBase={reportTarget ? `/admin/${reportTarget.type === "mock_exam" ? "mock-exam" : "paper"}/${reportTarget.id}` : null}
        filename={reportTarget?.name}
        onClose={() => setReportTarget(null)}
        onRechecked={load}
      />
      <BulkWebCheckDialog open={bulkOpen} onClose={() => { setBulkOpen(false); load(); }} onFinished={load} />
    </Box>
  );
}
